import { useState } from "react";
import { api } from "../api/client";
import { haptic } from "../lib/tg";
import { notifyError } from "../lib/ui";
import { useUserStore } from "../store/user";
import type { Gender, User } from "../types";

const CLOTHING_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

// Карточку можно закрыть — тогда она не возвращается на этом устройстве.
const DISMISS_KEY = "size_intro_dismissed";

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;   // приватный режим — показываем, это не критично
  }
}

/**
 * Знакомство на главной: пол и размер в два тапа, без ухода в профиль.
 *
 * Зачем отдельным блоком: персональные ленты («ваш размер») и подсветка в
 * каталоге работают от профиля, а заполнить его раньше предлагалось только
 * внутри профиля — кто туда не заходил, фичи не видел никогда.
 *
 * Сохраняется сразу по тапу на размер: кнопка «Сохранить» здесь лишний шаг,
 * а ошибиться нечем — выбор всегда можно поменять в профиле.
 */
export default function SizeIntro() {
  const reloadUser = useUserStore((s) => s.load);
  const [gender, setGender] = useState<Gender | "">("");
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(isDismissed);

  if (hidden) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* не сохранилось — вернётся при следующем открытии, не страшно */
    }
    setHidden(true);
  };

  const pickSize = async (size: string) => {
    setSaving(true);
    try {
      await api<User>("/me", {
        method: "PATCH",
        body: JSON.stringify({ gender: gender || null, clothing_size: size }),
      });
      haptic.success();
      await reloadUser();   // ленты и подсветка в каталоге оживают сразу
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="intro">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <p className="section-label" style={{ margin: 0 }}>Подберём ваш размер</p>
          <p style={{ margin: "6px 0 14px", fontSize: 14, color: "var(--hint)" }}>
            Два тапа — и в каталоге подсветится то, что вам подойдёт.
          </p>
        </div>
        <button className="intro__close" onClick={dismiss} aria-label="Скрыть">×</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        {([["male", "Мужской"], ["female", "Женский"]] as [Gender, string][]).map(([val, label]) => (
          <button
            key={val}
            onClick={() => { haptic.select(); setGender(gender === val ? "" : val); }}
            className={`chip${gender === val ? " chip--on" : ""}`}
            style={{ flex: 1 }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {CLOTHING_SIZES.map((s) => (
          <button key={s} className="chip" disabled={saving} onClick={() => pickSize(s)}>
            {s}
          </button>
        ))}
      </div>
    </section>
  );
}
